import { HttpClient } from '@angular/common/http';
import { Injectable, Optional, Type } from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import * as _ from 'lodash';
import { isString } from 'lodash';
import { Observable } from 'rxjs';
import { Entity } from 'src/app/shared/models/entities';
import { environment } from 'src/environments/environment';
import { DynacrudApi, MovementRequest, SearchRequest } from '../models/dynacrud';
import { ContextService } from '../services/context.service';
import { paramsCustom } from '../models/route-config';
import { selectFeatureCode } from 'src/app/store/shared/shared.reducers';

export interface DynacrudApiInfo {
    path: string;
    full: boolean;
    hasState?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class DynacrudApiWrapper {

    // DIAGNOSTIC (filter-leak): current route code, stamped into every
    // searchRequest as `_route` at send time. Combined with `filter._src`
    // (the builder) this names the CONSUMER in the backend error log:
    // `_src` = which component built the filter, `_route` = which route slot
    // actually sent it. Backend ignores unknown JSON props, so it is inert.
    private currentRouteCode: string | undefined;

    // `store` is @Optional so this root service stays injectable in specs that
    // don't import StoreModule/provideMockStore (it's pulled in transitively by
    // many components/services). Without Store the `_route` tag is simply
    // omitted — harmless in tests; production always has the Store.
    constructor(
        protected http: HttpClient,
        protected contextService: ContextService,
        @Optional() protected store: Store<any> | null = null,
    ) {
        this.store?.pipe(select(selectFeatureCode)).subscribe(c => this.currentRouteCode = c);
    }

    private sourceInfoCache: { [key: string]: DynacrudApiInfo } = {
        // expansion: { path: 'expansions', full: false},
    };

    // DIAGNOSTIC (filter-leak): base64-encode the searchRequest, stamping the
    // consuming route code so it surfaces in backend logs. Does not mutate the
    // caller's object (the searchRequest may be shared store state).
    protected encodeSearchRequest(searchRequest: SearchRequest): string {
        const tagged = this.currentRouteCode ? { ...searchRequest, _route: this.currentRouteCode } : searchRequest;
        return btoa(JSON.stringify(tagged));
    }

    protected getBasePath() {
        return environment.apiUrl;
    }

    // TODO: getDefaultImpl
    public getDefaultImpl<T extends Entity>(type: Type<T> | string, params?: paramsCustom): DynaCrudService<T> {

        let typeString: string = type.toString();

        if (!isString(type)) {
            typeString = type.constructor.name.toString();
        }

        const apiInfo: DynacrudApiInfo | undefined = this.getInfo(typeString as any);
        typeString = apiInfo ? apiInfo.path : typeString;

        const relativeUrl: string = typeString;
        const me = this;
        return {
            delete(entity: T): Observable<DynacrudApi<T>> {
                return me.http.delete<DynacrudApi<T>>(me.url(relativeUrl) + `/${entity.id}`);
            },

            /**
             * Deletes several records in one request: `DELETE <path>/bulk/<id>,<id>,...`, the shape
             * dynacrud's own `DefaultBulkCrudController` exposes. The backend runs the whole thing in
             * one transaction, so a refusal on any id rolls back every deletion in the request.
             *
             * <p>Takes IDS rather than entities, unlike `delete` above: the one caller deleting a
             * customer's whole group of orders has the ids and not always the rows, and a list of ids
             * is what the URL carries either way.
             */
            deleteBulk(ids: any[]): Observable<DynacrudApi<T[]>> {
                return me.http.delete<DynacrudApi<T[]>>(me.url(relativeUrl) + `/bulk/${ids.join(',')}`);
            },

            save(entity: T): Observable<DynacrudApi<T>> {
                if (entity.id) {
                    return me.http.put<DynacrudApi<T>>(me.url(relativeUrl) + `/${entity.id}`, entity);
                } else {
                    return me.http.post<DynacrudApi<T>>(me.url(relativeUrl), entity);
                }
            },

            get(id: any): Observable<DynacrudApi<T>> {
                return me.http.get<DynacrudApi<T>>(me.url(relativeUrl) + `/${id}`);
            },
            search(searchRequest?: SearchRequest): Observable<DynacrudApi<T[]>> {
                if (!searchRequest) {
                    return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl));
                } else {
                    if (params) {
                        return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl), { params: { ...params, searchRequest: me.encodeSearchRequest(searchRequest) } });
                    }
                    return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl), { params: { searchRequest: me.encodeSearchRequest(searchRequest) } });
                }
            },
            searchCustomer(searchRequest: SearchRequest, movementRequest?: MovementRequest) {
                if (!movementRequest && !searchRequest ) {
                    return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl));
                } else {
                    return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl), { params: { searchRequest: btoa(JSON.stringify(searchRequest)), movementRequest: btoa(JSON.stringify(movementRequest))  } });
                }
                
            },
            lookup(text: string, pageSize: number): Observable<DynacrudApi<T[]>> {
                return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl));
            }
        } as DynaCrudService<T>;


    }

    private url(relativeUrl: string): string {
        return this.getBasePath() + '/v2/' + (relativeUrl);
    }

    // TODO: getFor searchRequest
    public getFor<T extends Entity>(type: Type<T> | string | { value: string; s: boolean }, params?: paramsCustom): DynaCrudService<T> {
        let typeString: any = undefined;

        if (!isString(type) && !(type as any)?.value) {
            typeString = type.constructor.name.toString();
        }

        if (isString(type)) {
            typeString = type.toString();
        }

        if ((type as { value: string; s: boolean })['value']) {
            typeString = type;
        }

        const apiInfo: DynacrudApiInfo | undefined = this.getInfo(typeString);
        typeString = apiInfo ? apiInfo.path : typeString?.value || typeString;

        // Use the DI-injected ContextService (not the static `instance()`) so each Angular
        // root — including each test's TestBed — sees a fresh injector. The static
        // singleton would otherwise hold a destroyed-injector reference across specs,
        // causing flaky NG0205 ("Injector has already been destroyed") in tests whose
        // ngOnInit eagerly calls `.getFor(...)`.
        const service = this.contextService.getService(typeString?.value || typeString, 'DynaCrudService');

        if (service) {
            return service;
        }
        const relativeUrl: string = typeString;

        const me = this;
        return {

            get url(): string {
                return me.getBasePath() + '/v2/' + (relativeUrl);
            },

            delete(entity: T): Observable<DynacrudApi<T>> {
                return me.http.delete<DynacrudApi<T>>(me.url(relativeUrl) + `/${entity.id}`);
            },

            /**
             * Deletes several records in one request: `DELETE <path>/bulk/<id>,<id>,...`, the shape
             * dynacrud's own `DefaultBulkCrudController` exposes. The backend runs the whole thing in
             * one transaction, so a refusal on any id rolls back every deletion in the request.
             *
             * <p>Takes IDS rather than entities, unlike `delete` above: the one caller deleting a
             * customer's whole group of orders has the ids and not always the rows, and a list of ids
             * is what the URL carries either way.
             */
            deleteBulk(ids: any[]): Observable<DynacrudApi<T[]>> {
                return me.http.delete<DynacrudApi<T[]>>(me.url(relativeUrl) + `/bulk/${ids.join(',')}`);
            },

            save(entity: T): Observable<DynacrudApi<T>> {
                if (entity.id) {
                    return me.http.put<DynacrudApi<T>>(me.url(relativeUrl) + `/${entity.id}`, entity);
                } else {
                    return me.http.post<DynacrudApi<T>>(me.url(relativeUrl), entity);
                }
            },

            get(id: any): Observable<DynacrudApi<T>> {
                return me.http.get<DynacrudApi<T>>(me.url(relativeUrl) + `/${id}`);
            },
            search(searchRequest?: SearchRequest): Observable<DynacrudApi<T[]>> {
        
                if (!searchRequest) {
                    return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl));
                } else {
                    if (params) {
                        return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl), { params: { ...params,  searchRequest: me.encodeSearchRequest(searchRequest) } });
                    }
                    return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl), { params: { searchRequest: me.encodeSearchRequest(searchRequest) } });
                }
            },

            searchCustomer(searchRequest: SearchRequest, movementRequest?: MovementRequest) {
                if (!searchRequest && !movementRequest) {
                    return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl));
                } else {
                    return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl), { params: { searchRequest: btoa(JSON.stringify(searchRequest)), movementRequest: btoa(JSON.stringify(movementRequest))  } });
                    // return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl), { params: { searchRequest: btoa(JSON.stringify(searchRequest)) } });
                }
                
            },

            lookup(text: string, pageSize: number): Observable<DynacrudApi<T[]>> {
                return me.http.get<DynacrudApi<T[]>>(me.url(relativeUrl));
            }
        } as DynaCrudService<T>;
    }

    getInfo(keyValue: { [key: string]: any } /** puo essere 's' || 'value' || string */): DynacrudApiInfo | undefined {
        const key = keyValue['value'] || keyValue;
        if (key) {
            const source: string = key.toString();
            let sourceInfo: DynacrudApiInfo = this.sourceInfoCache[source];
            if (!sourceInfo) {
                let sourcePath = source;
                if (sourcePath.indexOf('/') == -1) {
                    sourcePath = _.camelCase(source);
                    if (sourcePath.endsWith('y')) {
                        sourcePath = sourcePath.substr(0, sourcePath.lastIndexOf('y')) + 'ies';
                    } else if (!sourcePath.endsWith('s') && !keyValue['s']) {
                        sourcePath = sourcePath + 's';
                    } else {
                        sourcePath = sourcePath;
                    }
                }

                sourceInfo = { path: sourcePath, full: true };
                this.sourceInfoCache[source] = sourceInfo;
            }
            return this.sourceInfoCache[source];
        }
        return undefined;

    }




}


export interface DynaCrudService<T extends Entity> {
    delete(entity: T): Observable<DynacrudApi<T>>;
    /**
     * Delete several records in one request. OPTIONAL, and deliberately so: this interface is also
     * implemented by hand-written services bound to endpoints that expose no bulk delete at all
     * (the kiosk fleet, market reference status, the weak-data base), and making it required would
     * force each of them to declare a method its endpoint does not answer. The two generic
     * factories above always provide it; a caller reaching for it on a service it did not obtain
     * from them must check.
     */
    deleteBulk?(ids: any[]): Observable<DynacrudApi<T[]>>;
    search(searchRequest?: SearchRequest ): Observable<DynacrudApi<T[]>>;
    searchCustomer(searchRequest?: SearchRequest, movementRequest?: MovementRequest ): Observable<DynacrudApi<T[]>>;
    get(id: any): Observable<DynacrudApi<T>>;
    save(entity: T): Observable<DynacrudApi<T>>;
}

